import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import {
    Building2,
    CircleUser,
    DoorOpen,
    Landmark,
    ListChecks,
    LogOut,
    Route,
    Settings,
    SlidersHorizontal,
    UserRoundCog,
    Users,
    UsersRound,
    Wallet,
    Waypoints,
    Workflow,
} from 'lucide-react';
import { useAuth } from '../../context/authContext';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { isAllOrganisationsSelected } from '../../utils/organisation';
import { cn } from '../../utils';
import YavrioLogo2025 from '../../images/YavrioLogo2025';

// Switch between 'refined', 'minimal', or 'classic' to compare icon styles.
const SIDEBAR_ICON_SET = 'refined';

const iconSets = {
    classic: {
        payments: Landmark,
        settings: Settings,
        users: Users,
        workflows: Workflow,
        orgSettings: Building2,
        logout: LogOut,
    },
    refined: {
        payments: Wallet,
        settings: CircleUser,
        users: UserRoundCog,
        workflows: Waypoints,
        orgSettings: Building2,
        logout: LogOut,
    },
    minimal: {
        payments: ListChecks,
        settings: SlidersHorizontal,
        users: UsersRound,
        workflows: Route,
        orgSettings: Building2,
        logout: DoorOpen,
    },
};

const icons = iconSets[SIDEBAR_ICON_SET] || iconSets.refined;

const NavIcon = ({ icon: Icon }) => (
    <Icon className="w-[18px] h-[18px] shrink-0 opacity-90" strokeWidth={1.5} />
);

const SidebarNavItem = ({ to, icon, text, end = false }) => (
    <li>
        <NavLink
            to={to}
            end={end}
            className={({ isActive }) =>
                cn(
                    'block rounded-md py-1.5 pl-2 -ml-2 border-l transition-colors',
                    isActive
                        ? 'border-yavrio-green/50 text-white/95'
                        : 'border-transparent text-white/60 hover:text-white/85'
                )
            }
        >
            <div className="flex flex-row gap-3 items-center pl-2">
                {icon}
                {text}
            </div>
        </NavLink>
    </li>
);

const MenuSection = ({ title, links }) => (
    <div className="mb-8">
        <p className="text-xs font-semibold tracking-widest text-white/40 uppercase mb-3">
            {title}
        </p>
        <ul className="flex flex-col gap-1">
            {links.map((link) => (
                <SidebarNavItem
                    key={link.url}
                    to={link.url}
                    end={link.end}
                    icon={link.icon}
                    text={link.text}
                />
            ))}
        </ul>
    </div>
);

const Sidebar = () => {

    const { user, setUser, setAuthenticated } = useAuth();
    const { advancedFeaturesEnabled } = useFeatureAccess();
    // Organisation-scoped admin pages are hidden while viewing all organisations.
    const showAdminSection = user.role === 'Admin' && !isAllOrganisationsSelected(user);

    const menuSections = [
        {
            title: 'Payments',
            links: [
                { text: 'Payments', url: '/payments', icon: <NavIcon icon={icons.payments} /> },
            ],
        },
        {
            title: 'My Account',
            links: [
                { text: 'Settings', url: '/settings', end: true, icon: <NavIcon icon={icons.settings} /> },
            ],
        },
        ...(showAdminSection ? [{
            title: 'Administration',
            links: [
                ...(advancedFeaturesEnabled
                    ? [{ text: 'Organisation Settings', url: '/org-settings', end: true, icon: <NavIcon icon={icons.orgSettings} /> }]
                    : []),
                { text: 'User Management', url: '/users', end: true, icon: <NavIcon icon={icons.users} /> },
                ...(advancedFeaturesEnabled
                    ? [{ text: 'Workflows', url: '/workflows', icon: <NavIcon icon={icons.workflows} /> }]
                    : []),
            ],
        }] : []),
    ];

    const handleLogout = () => {
        setUser({
            email: '',
            role: '',
            isActive: false,
            twoFactorEnabled: false,
        });
        setAuthenticated(false);
    };

    return (
        <aside
            className="w-[17rem] shrink-0 p-6 text-white flex flex-col h-full"
            style={{ backgroundColor: '#2E2E2E' }}
        >
            <div className="mb-8">
                <Link to="/payments">
                    <YavrioLogo2025 className="h-9 w-auto" />
                </Link>
                <p className="text-white font-semibold text-lg mt-10 mb-0 leading-snug max-w-[80%]">
                    Payment Authorisation Portal
                </p>
            </div>

            <div className="flex-1">
                <hr className="opacity-20 mb-8 w-4/5" />
                {menuSections.map((section) => (
                    <MenuSection key={section.title} title={section.title} links={section.links} />
                ))}
            </div>

            <div className="text-white border-t border-white/10 pt-6">
                <Link to="#" onClick={handleLogout}>
                    <div className="flex flex-row gap-3 items-center mb-6">
                        <NavIcon icon={icons.logout} />
                        Logout
                    </div>
                </Link>
                <Link to="mailto:support@yavr.io">Need help?</Link>
                <p className="text-white mt-6 mb-0">Yavrio Ltd. © 2026</p>
            </div>
        </aside>
    );
};

export default Sidebar;
